"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableColumnBarCharts = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var sdk_model_1 = require("@gooddata/sdk-model");
var util_1 = require("@gooddata/util");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var axis_1 = require("../../constants/axis");
var bucket_1 = require("../../constants/bucket");
var uiConfig_1 = require("../../constants/uiConfig");
var ImplicitDrillDownHelper_1 = require("../../utils/ImplicitDrillDownHelper");
var bucketHelper_1 = require("../../utils/bucketHelper");
var propertiesHelper_1 = require("../../utils/propertiesHelper");
var columnBarChartUiConfigHelper_1 = require("../../utils/uiConfigHelpers/columnBarChartUiConfigHelper");
var PluggableBaseChart_1 = require("./baseChart/PluggableBaseChart");
var drillDownUtil_1 = require("./drillDownUtil");
var PluggableColumnBarCharts = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableColumnBarCharts, _super);
    function PluggableColumnBarCharts(props) {
        var _this = _super.call(this, props) || this;
        // set default to DUAL to get the full supported props list
        // and will be updated in getExtendedReferencePoint
        _this.axis = axis_1.AXIS.DUAL;
        _this.supportedPropertiesList = _this.getSupportedPropertiesList();
        return _this;
    }
    PluggableColumnBarCharts.prototype.getUiConfig = function () {
        var config = this.isMultipleDatesEnabled()
            ? uiConfig_1.COLUMN_BAR_CHART_UICONFIG_WITH_MULTIPLE_DATES
            : uiConfig_1.COLUMN_BAR_CHART_UICONFIG;
        return cloneDeep_1.default(config);
    };
    PluggableColumnBarCharts.prototype.getExtendedReferencePoint = function (referencePoint) {
        var _this = this;
        // reset the list to retrieve full 'referencePoint.properties.controls'
        this.supportedPropertiesList = this.getSupportedPropertiesList();
        return _super.prototype.getExtendedReferencePoint.call(this, referencePoint).then(function (ext) {
            var _a, _b;
            var newExt = propertiesHelper_1.setSecondaryMeasures(ext, _this.secondaryAxis);
            _this.axis = (_b = (_a = newExt === null || newExt === void 0 ? void 0 : newExt.uiConfig) === null || _a === void 0 ? void 0 : _a.axis) !== null && _b !== void 0 ? _b : axis_1.AXIS.PRIMARY;
            // filter out unnecessary stacking props for some specific cases such as one measure or empty stackBy
            _this.supportedPropertiesList = propertiesHelper_1.removeImmutableOptionalStackingProperties(newExt, _this.getSupportedPropertiesList());
            newExt = propertiesHelper_1.getReferencePointWithSupportedProperties(newExt, _this.supportedPropertiesList);
            return columnBarChartUiConfigHelper_1.setColumnBarChartUiConfig(newExt, _this.intl);
        });
    };
    PluggableColumnBarCharts.prototype.isOpenAsReportSupported = function () {
        return (_super.prototype.isOpenAsReportSupported.call(this) &&
            !haveManyViewItems(this.currentInsight) &&
            !propertiesHelper_1.isStackingMeasure(this.visualizationProperties) &&
            !propertiesHelper_1.isStackingToPercent(this.visualizationProperties));
    };
    PluggableColumnBarCharts.prototype.adjustIntersectionForColumnBar = function (source, event) {
        var stackBucket = sdk_model_1.insightBucket(source, sdk_ui_1.BucketNames.STACK);
        var hasStackByAttributes = stackBucket && !sdk_model_1.bucketIsEmpty(stackBucket);
        var intersection = event.drillContext.intersection;
        return hasStackByAttributes ? util_1.arrayUtils.shiftArrayRight(intersection) : intersection;
    };
    PluggableColumnBarCharts.prototype.addFiltersForColumnBar = function (source, drillConfig, event) {
        var clicked = ImplicitDrillDownHelper_1.drillDownFromAttributeLocalId(drillConfig);
        var reorderedIntersection = this.adjustIntersectionForColumnBar(source, event);
        var cutIntersection = sdk_ui_1.getIntersectionPartAfter(reorderedIntersection, clicked);
        return drillDownUtil_1.addIntersectionFiltersToInsight(source, cutIntersection);
    };
    PluggableColumnBarCharts.prototype.getInsightWithDrillDownApplied = function (source, drillDownContext) {
        var withFilters = this.addFiltersForColumnBar(source, drillDownContext.drillDefinition, drillDownContext.event);
        return drillDownUtil_1.modifyBucketsAttributesForDrillDown(withFilters, drillDownContext.drillDefinition);
    };
    PluggableColumnBarCharts.prototype.configureBuckets = function (extendedReferencePoint) {
        var _a, _b, _c, _d, _e;
        if (this.isMultipleDatesEnabled()) {
            this.configureBucketsWithMultipleDates(extendedReferencePoint);
            return;
        }
        var buckets = (_a = extendedReferencePoint === null || extendedReferencePoint === void 0 ? void 0 : extendedReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var measures = bucketHelper_1.getFilteredMeasuresForStackedCharts(buckets);
        var dateItems = bucketHelper_1.getDateItems(buckets);
        var mainDateItem = bucketHelper_1.getMainDateItem(dateItems);
        var categoriesCount = (_e = (_d = (_c = (_b = extendedReferencePoint.uiConfig) === null || _b === void 0 ? void 0 : _b.buckets) === null || _c === void 0 ? void 0 : _c[sdk_ui_1.BucketNames.VIEW]) === null || _d === void 0 ? void 0 : _d.itemsLimit) !== null && _e !== void 0 ? _e : uiConfig_1.MAX_CATEGORIES_COUNT;
        var allAttributesWithoutStacks = bucketHelper_1.getAllCategoriesAttributeItems(buckets);
        var allAttributesWithoutStacksWithDatesHandled = bucketHelper_1.removeDivergentDateItems(allAttributesWithoutStacks, mainDateItem);
        var views = allAttributesWithoutStacksWithDatesHandled.slice(0, categoriesCount);
        var hasDateItemInViewByBucket = views.some(bucketHelper_1.isDateBucketItem);
        var stackItemIndex = categoriesCount;
        var stacks = bucketHelper_1.getStackItems(buckets);
        if (dateItems.length && !hasDateItemInViewByBucket) {
            var extraViewItems = allAttributesWithoutStacksWithDatesHandled.slice(0, categoriesCount - 1);
            views = tslib_1.__spreadArrays([mainDateItem], extraViewItems);
            stackItemIndex = categoriesCount - 1;
        }
        var hasSomeRemainingAttributes = allAttributesWithoutStacksWithDatesHandled.length > stackItemIndex;
        if (!stacks.length && measures.length <= 1 && hasSomeRemainingAttributes) {
            stacks = allAttributesWithoutStacksWithDatesHandled
                .slice(stackItemIndex, allAttributesWithoutStacksWithDatesHandled.length)
                .filter(bucketHelper_1.isNotDateBucketItem)
                .slice(0, uiConfig_1.MAX_STACKS_COUNT);
        }
        set_1.default(extendedReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: measures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: views,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.STACK,
                items: stacks,
            },
        ]);
    };
    PluggableColumnBarCharts.prototype.configureBucketsWithMultipleDates = function (extendedReferencePoint) {
        var _a = this.getMeasuresViewStackBucketItems(extendedReferencePoint), measures = _a.measures, views = _a.views, stacks = _a.stacks;
        set_1.default(extendedReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: measures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: views,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.STACK,
                items: stacks,
            },
        ]);
    };
    PluggableColumnBarCharts.prototype.canPutAttributeToViewBy = function (currentAttribute, firstAttribute, viewByCount, viewByMaxItemCount) {
        var isFirstAttributeDate = bucketHelper_1.isDateBucketItem(firstAttribute);
        var isCurrentAttributeDate = bucketHelper_1.isDateBucketItem(currentAttribute);
        var sameDateDimension = bucketHelper_1.hasSameDateDimension(firstAttribute, currentAttribute);
        return ((!isFirstAttributeDate || !isCurrentAttributeDate || sameDateDimension) &&
            viewByCount < viewByMaxItemCount);
    };
    PluggableColumnBarCharts.prototype.getMeasuresViewStackBucketItems = function (extendedReferencePoint) {
        var _a;
        var buckets = (_a = extendedReferencePoint === null || extendedReferencePoint === void 0 ? void 0 : extendedReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var measures = bucketHelper_1.getFilteredMeasuresForStackedCharts(buckets);
        var viewByMaxItemCount = this.getViewByMaxItemCount(extendedReferencePoint);
        var stackByMaxItemCount = this.getStackByMaxItemCount(extendedReferencePoint);
        var allAttributesWithoutStacks = bucketHelper_1.getAllCategoriesAttributeItems(buckets);
        var stacks = bucketHelper_1.getStackItems(buckets, [bucket_1.ATTRIBUTE, bucket_1.DATE]);
        var firstAttribute = allAttributesWithoutStacks[0], remainingAttributes = allAttributesWithoutStacks.slice(1);
        var views = firstAttribute ? [firstAttribute] : [];
        var possibleStacks = [];
        for (var _i = 0, remainingAttributes_1 = remainingAttributes; _i < remainingAttributes_1.length; _i++) {
            var currentAttribute = remainingAttributes_1[_i];
            var canPutToViewBy = this.canPutAttributeToViewBy(currentAttribute, firstAttribute, views.length, viewByMaxItemCount);
            if (canPutToViewBy) {
                views.push(currentAttribute);
            }
            else {
                possibleStacks.push(currentAttribute);
            }
        }
        if (!stacks.length && measures.length <= 1) {
            var finalStacks = tslib_1.__spreadArrays(stacks, possibleStacks).slice(0, stackByMaxItemCount);
            return { measures: measures, views: views, stacks: finalStacks };
        }
        return { measures: measures, views: views, stacks: stacks };
    };
    PluggableColumnBarCharts.prototype.getViewByMaxItemCount = function (extendedReferencePoint) {
        var _a, _b, _c, _d;
        return ((_d = (_c = (_b = (_a = extendedReferencePoint.uiConfig) === null || _a === void 0 ? void 0 : _a.buckets) === null || _b === void 0 ? void 0 : _b[sdk_ui_1.BucketNames.VIEW]) === null || _c === void 0 ? void 0 : _c.itemsLimit) !== null && _d !== void 0 ? _d : uiConfig_1.MAX_CATEGORIES_COUNT);
    };
    PluggableColumnBarCharts.prototype.getStackByMaxItemCount = function (extendedReferencePoint) {
        var _a, _b, _c, _d;
        return (_d = (_c = (_b = (_a = extendedReferencePoint.uiConfig) === null || _a === void 0 ? void 0 : _a.buckets) === null || _b === void 0 ? void 0 : _b[sdk_ui_1.BucketNames.STACK]) === null || _c === void 0 ? void 0 : _c.itemsLimit) !== null && _d !== void 0 ? _d : uiConfig_1.MAX_STACKS_COUNT;
    };
    return PluggableColumnBarCharts;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableColumnBarCharts = PluggableColumnBarCharts;
function haveManyViewItems(insight) {
    return sdk_model_1.bucketsItems(sdk_model_1.insightBuckets(insight, sdk_ui_1.BucketNames.VIEW)).length > 1;
}
//# sourceMappingURL=PluggableColumnBarCharts.js.map